import { PLATFORM_LABELS, type PushPlatform } from '@/features/push-credentials';
import type { App } from '@/services/db/app/entity';
import { APP_PLATFORMS } from './platform-state';

export const NO_PUSH_CREDENTIAL_TOOLTIP = 'Add iOS or Android push credentials to this app to send to it.';

const PLATFORM_ICONS = { ios: 'apple', android: 'android' } as const;

/**
 * A test sends through a platform's own credential, so a platform registered
 * without one can no more be tested than an app with no platforms at all.
 */
export function appSupportsPlatform(app: App | undefined, platform: PushPlatform): boolean {
  if (app == null) {
    return false;
  }
  const entry = app.platforms.find((candidate) => candidate.platform === platform);
  return entry?.credential_id != null;
}

export function hasPushCredential(app: App): boolean {
  return app.platforms.some((platform) => platform.credential_id != null);
}

export function appSelectOptions(apps: readonly App[]) {
  return apps.map((app) => {
    const option = { label: app.name, value: String(app.id) };
    return hasPushCredential(app) ? option : { ...option, isDisabled: true, tooltip: NO_PUSH_CREDENTIAL_TOOLTIP };
  });
}

/**
 * Falls back to the default or first app even when neither can send, so the
 * field still names the app whose credentials need attention.
 */
export function resolveSelectedApp(apps: readonly App[], pickedId: number | null): App | undefined {
  const picked = pickedId == null ? undefined : apps.find((app) => app.id === pickedId);
  if (picked != null) {
    return picked;
  }
  const defaultApp = apps.find((app) => app.is_default);
  const sendable =
    defaultApp != null && hasPushCredential(defaultApp) ? defaultApp : apps.find((app) => hasPushCredential(app));
  return sendable ?? defaultApp ?? apps[0];
}

/** A platform this app cannot be tested on is disabled rather than hidden, so the pair reads as its coverage. */
export function platformOptionsFor(app: App | undefined) {
  return APP_PLATFORMS.map((platform) => ({
    value: platform,
    label: PLATFORM_LABELS[platform],
    icon: PLATFORM_ICONS[platform],
    isDisabled: !appSupportsPlatform(app, platform),
  }));
}

export function resolvePlatform(app: App | undefined, chosen: PushPlatform | null): PushPlatform | null {
  const supported = APP_PLATFORMS.filter((platform) => appSupportsPlatform(app, platform));
  if (supported.length === APP_PLATFORMS.length) {
    return chosen ?? 'ios';
  }
  return supported[0] ?? null;
}
